import { useMemo } from 'react';
import PropTypes from 'prop-types';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';

// Components
import SchedulerPost from '@/components/pages/scheduler/feed/SchedulerPost';
import LoadMoreButton from '@/components/composite/Button/LoadMoreButton';

const SchedulerPostsList = ({
    schedulerPosts,
    isLoading,
    isLoadMore,
    isLoadingMore,
    loadMore,
    setDeleteModal,
    activeFilter,
    onClickEditPost = () => {
        /** */
    },
    reorderPosts = () => {
        /** */
    },
    openMediaLibrary = false
}) => {
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const schedulerPostsData = useMemo(
        () =>
            schedulerPosts?.filter((schedulerPost) => {
                if (schedulerPost.scheduled_at) {
                    if (
                        activeFilter.value !== 'all' &&
                        schedulerPost.id.toString().split('-')[0] === 'draggable'
                    ) {
                        return false;
                    }

                    return schedulerPost;
                }
            }),
        [activeFilter.value, schedulerPosts]
    );
    const userTimezone = user?.settings?.timezone ?? 'Europe/Paris';
    const { formatDateTime } = useFormatDateTime(
        user?.settings?.timezone,
        user?.settings?.platform_language
    );

    if (isLoading) {
        return (
            <div className="w-full">
                {[...Array(4).keys()].map((index) => (
                    <SchedulerPost
                        key={index}
                        isLoading={isLoading}
                        className="mb-2"
                        openMediaLibrary={openMediaLibrary}
                    />
                ))}
            </div>
        );
    }

    return (
        <div className="lg:scrollbar h-full w-full space-y-3">
            {schedulerPostsData?.map((schedulerPost, index) => (
                <div
                    key={schedulerPost.id}
                    className="flex flex-col space-y-3"
                    data-testid={`feed-view-post-${schedulerPost.id}`}>
                    {(index === 0 ||
                        formatDateTime(schedulerPost?.scheduled_at, user?.settings.date_format) !==
                            formatDateTime(
                                schedulerPosts?.[index - 1]?.scheduled_at,
                                user?.settings.date_format
                            )) && (
                        <p className="mt-6 text-sm capitalize">
                            {formatDateTime(
                                schedulerPost?.scheduled_at,
                                `${user?.settings.date_format}_inLetters_noYear`
                            )}
                        </p>
                    )}
                    <SchedulerPost
                        schedulerPost={schedulerPost}
                        isLoading={isLoading}
                        onRemovePost={(id) => {
                            reorderPosts(schedulerPosts.filter((post) => post.id !== id));
                        }}
                        timezone={userTimezone}
                        timeFormat={user?.settings?.time_format}
                        setDeleteModal={setDeleteModal}
                        onClickEditPost={onClickEditPost}
                        openMediaLibrary={openMediaLibrary}
                    />
                </div>
            ))}
            {isLoadMore ? (
                <LoadMoreButton
                    className="mt-2 w-full"
                    loadMoreItems={loadMore}
                    isLoadingMore={isLoadingMore}
                />
            ) : null}
        </div>
    );
};

SchedulerPostsList.propTypes = {
    schedulerPosts: PropTypes.array,
    isLoading: PropTypes.bool,
    isLoadMore: PropTypes.bool,
    isLoadingMore: PropTypes.bool,
    loadMore: PropTypes.func,
    setDeleteModal: PropTypes.func,
    openMediaLibrary: PropTypes.bool
};

export default SchedulerPostsList;
